import { readonly, ref } from 'vue';
import { getPaymentTrustedAccountOptions } from '@/services/paymentService';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';
import { getCookieJSON, setCookie } from '@/utils/cookieUtils';

interface PaymentTrustedAccountOptionItem {
    id: number;
    label: string;
}

/** `null` means All; an empty array means no Trust Account is selected. */
export type PaymentTrustedAccountSelection = number[] | null;

interface PaymentTrustedAccountFilterSettings {
    mode?: 'all' | 'selected';
    payeeIds?: number[];
}

const FILTER_COOKIE_NAME = 'payments_trusted_account_filter';
const FILTER_COOKIE_EXPIRY_DAYS = 365;

const options = ref<PaymentTrustedAccountOptionItem[]>([]);
let loaded = false;
let loadedScopeKey: string | null = null;
let loadGeneration = 0;
let loadingRequest: Promise<void> | null = null;

function currentScopeKey(): string {
    const creditorId = useSuperAdminCreditorAccessStore().context?.creditorId;
    return creditorId != null ? `creditor:${creditorId}` : 'none';
}

function filterCookieName(): string {
    const creditorId = useSuperAdminCreditorAccessStore().context?.creditorId;
    return creditorId != null
        ? `${FILTER_COOKIE_NAME}_${creditorId}`
        : FILTER_COOKIE_NAME;
}

/**
 * Loads trusted-account options for the current impersonation/creditor scope.
 * Refetches only when Payments mounts (or loadOptions is called) and the
 * cached scope differs from the current one.
 */
async function loadOptions(): Promise<void> {
    const scopeKey = currentScopeKey();
    if (loaded && loadedScopeKey === scopeKey) return;

    const generation = loadGeneration + 1;
    loadGeneration = generation;
    loaded = false;
    loadedScopeKey = null;
    options.value = [];
    loadingRequest = null;

    loadingRequest = getPaymentTrustedAccountOptions()
        .then((accounts) => {
            if (generation !== loadGeneration) return;

            options.value = accounts.map((account) => ({
                id: account.id,
                label: account.payeeName,
            }));
            loaded = true;
            loadedScopeKey = scopeKey;
        })
        .catch(() => {
            if (generation !== loadGeneration) return;
            options.value = [];
        })
        .finally(() => {
            if (generation === loadGeneration) {
                loadingRequest = null;
            }
        });

    await loadingRequest;
}

export function usePaymentTrustedAccountOptions() {
    return {
        options: readonly(options),
        loadOptions,
    };
}

function normalizePayeeIds(ids: unknown): number[] {
    if (!Array.isArray(ids)) return [];

    return [...new Set(ids.filter(
        (id): id is number => Number.isInteger(id) && id > 0,
    ))];
}

export function loadSavedPaymentTrustedAccountIds(): PaymentTrustedAccountSelection {
    const saved = getCookieJSON<PaymentTrustedAccountFilterSettings>(filterCookieName());
    if (!saved || saved.mode === 'all') return null;

    const payeeIds = normalizePayeeIds(saved.payeeIds);

    if (saved.mode === 'selected') return payeeIds;

    return payeeIds.length > 0 ? payeeIds : null;
}

export function savePaymentTrustedAccountIds(selection: PaymentTrustedAccountSelection): void {
    const settings: PaymentTrustedAccountFilterSettings = selection === null
        ? { mode: 'all' }
        : { mode: 'selected', payeeIds: selection };

    setCookie(
        filterCookieName(),
        settings as unknown as Record<string, unknown>,
        { days: FILTER_COOKIE_EXPIRY_DAYS },
    );
}
